export interface KolorcansBoxPreset {
    id: string;
    l: number;
    w: number;
    h: number;
}

export type KolorcansComponentRole = 'body' | 'lid' | 'door' | 'liner' | 'hardware';

export type KolorcansAssemblyFamily = '42-round' | '32-square' | '52-square' | '55-round' | '22-square';

export type KolorcansAutoPackSource = 'txt' | 'xlsx' | 'docx' | 'manual';

export interface KolorcansBoxAlias {
    key: string;
    boxPresetId: string;
    l: number;
    w: number;
    h: number;
}

export interface KolorcansComponentClassifier {
    id: string;
    role: KolorcansComponentRole;
    partPrefixes: string[];
    descriptionIncludes: string[];
}

export interface KolorcansAssemblyRule {
    id: string;
    family: KolorcansAssemblyFamily;
    label: string;
    priority: number;
    source: KolorcansAutoPackSource;
    confidence: 'high' | 'review';
    finishedPartPrefixes: string[];
    descriptionIncludes: string[];
    bodyBoxKey: string;
    lidBoxKey: string;
    lidCapacity: number;
    nestLidsWithBody: boolean;
    nonNestingLidDescriptionIncludes?: string[];
    nonNestingLidBoxKey?: string;
    nonNestingLidCapacity?: number;
}

export type KolorcansPalletPackingLayout = 'four-upright-remainder-long-side';

export interface KolorcansPalletPackingRule {
    id: string;
    label: string;
    boxPresetId: string;
    minimumBoxCount: number;
    maximumBoxCount: number;
    layout: KolorcansPalletPackingLayout;
}

export interface KolorcansData {
    customerId: string;
    finishingStationId: string;
    predefinedBoxes: KolorcansBoxPreset[];
    boxAliases: KolorcansBoxAlias[];
    componentClassifiers: KolorcansComponentClassifier[];
    assemblyRules: KolorcansAssemblyRule[];
    palletPackingRules: KolorcansPalletPackingRule[];
}

export const DEFAULT_KOLORCANS_BOX_PRESETS: KolorcansBoxPreset[] = [
    { id: 'box-25x25x38', l: 25, w: 25, h: 38 },
    { id: 'box-25x25x30', l: 25, w: 25, h: 30 },
    { id: 'box-25x25x27', l: 25, w: 25, h: 27 },
    { id: 'box-25x25x22', l: 25, w: 25, h: 22 },
    { id: 'box-25x25x19', l: 25, w: 25, h: 19 },
    { id: 'box-25x25x15', l: 25, w: 25, h: 15 },
    { id: 'box-19x19x39', l: 19, w: 19, h: 39 },
    { id: 'box-19x19x15', l: 19, w: 19, h: 15 },
    { id: 'box-15x15x22', l: 15, w: 15, h: 22 },
];

export const DEFAULT_KOLORCANS_BOX_ALIASES: KolorcansBoxAlias[] = [
    { key: '25x25x38', boxPresetId: 'box-25x25x38', l: 25, w: 25, h: 38 },
    { key: '25x25x30', boxPresetId: 'box-25x25x30', l: 25, w: 25, h: 30 },
    { key: '25x25x27', boxPresetId: 'box-25x25x27', l: 25, w: 25, h: 27 },
    { key: '25x25x22', boxPresetId: 'box-25x25x22', l: 25, w: 25, h: 22 },
    { key: '25x25x19', boxPresetId: 'box-25x25x19', l: 25, w: 25, h: 19 },
    { key: '25x25x15', boxPresetId: 'box-25x25x15', l: 25, w: 25, h: 15 },
    { key: '19x19x39', boxPresetId: 'box-19x19x39', l: 19, w: 19, h: 39 },
    { key: '19x19x15', boxPresetId: 'box-19x19x15', l: 19, w: 19, h: 15 },
    { key: '15x15x22', boxPresetId: 'box-15x15x22', l: 15, w: 15, h: 22 },
];

export const DEFAULT_KOLORCANS_PALLET_PACKING_RULES: KolorcansPalletPackingRule[] = [
    {
        id: '32-square-five-six',
        label: '32 gal square 5-6 box layout',
        boxPresetId: 'box-19x19x39',
        minimumBoxCount: 5,
        maximumBoxCount: 6,
        layout: 'four-upright-remainder-long-side',
    },
];

export const DEFAULT_KOLORCANS_DATA: KolorcansData = {
    customerId: '',
    finishingStationId: 'T710hDxWUQYANezZ1BiG',
    predefinedBoxes: DEFAULT_KOLORCANS_BOX_PRESETS,
    boxAliases: DEFAULT_KOLORCANS_BOX_ALIASES,
    componentClassifiers: [
        {
            id: 'body',
            role: 'body',
            partPrefixes: ['S7100', 'S7800', 'S8280', 'S8290', 'S7300', 'S8310', 'S8330', 'S7900'],
            descriptionIncludes: ['recep', 'receptacle', 'recpt'],
        },
        {
            id: 'liner',
            role: 'liner',
            partPrefixes: ['S7250', 'S7850', 'S7852', 'S8010', 'S8011', 'S8260', 'S7855'],
            descriptionIncludes: ['liner'],
        },
        {
            id: 'lid',
            role: 'lid',
            partPrefixes: ['S7110', 'S7120', 'S7130', 'S7140', 'S7150', 'S7200A', 'S7210A', 'S7220A', 'S7230A', 'S7240A', 'S7310', 'S7320', 'S7810', 'S7820', 'S7830', 'S8340'],
            descriptionIncludes: ['lid', 'top', 'dome', 'mushroom', 'funnel', 'swing', 'flat'],
        },
        {
            id: 'door',
            role: 'door',
            partPrefixes: ['S7121', 'S7141', 'S7811'],
            descriptionIncludes: ['door', 'flip'],
        },
        {
            id: 'hardware',
            role: 'hardware',
            partPrefixes: ['P-9001', 'S7HRDW'],
            descriptionIncludes: ['hardware', 'pedestal', 'lanyard'],
        },
    ],
    assemblyRules: [
        {
            id: '42-round-nesting',
            family: '42-round',
            label: '42 gal round nesting assembly',
            priority: 100,
            source: 'txt',
            confidence: 'high',
            finishedPartPrefixes: ['S7101A', 'S7102A', 'S7104A', 'S7105A', 'S7106A', 'S8281A', 'S8282A', 'S8284A', 'S8285A', 'S8286A'],
            descriptionIncludes: ['42', 'round'],
            bodyBoxKey: '25x25x30',
            lidBoxKey: '25x25x30',
            lidCapacity: 1,
            nestLidsWithBody: true,
        },
        {
            id: '42-round-two-way',
            family: '42-round',
            label: '42 gal round non-nesting 2-way assembly',
            priority: 110,
            source: 'txt',
            confidence: 'high',
            finishedPartPrefixes: ['S7103A', 'S8283A'],
            descriptionIncludes: ['42', '2 way', '2-way', 'two way', 'open'],
            bodyBoxKey: '25x25x30',
            lidBoxKey: '25x25x30',
            lidCapacity: 3,
            nestLidsWithBody: false,
        },
        {
            id: '32-square',
            family: '32-square',
            label: '32 gal square assembly',
            priority: 100,
            source: 'txt',
            confidence: 'high',
            finishedPartPrefixes: ['S7801A', 'S7802A', 'S7803A', 'S8291A', 'S8292A', 'S8294A'],
            descriptionIncludes: ['32', 'square'],
            bodyBoxKey: '19x19x39',
            lidBoxKey: '19x19x15',
            lidCapacity: 1,
            nestLidsWithBody: true,
        },
        {
            id: '52-square',
            family: '52-square',
            label: '52 gal square assembly',
            priority: 100,
            source: 'txt',
            confidence: 'high',
            finishedPartPrefixes: ['S7300', 'S7301A', 'S7302', 'S7302A', 'S7320', 'S8311A', 'S8312A'],
            descriptionIncludes: ['52', 'square'],
            bodyBoxKey: '25x25x30',
            lidBoxKey: '25x25x30',
            lidCapacity: 1,
            nestLidsWithBody: true,
            nonNestingLidDescriptionIncludes: ['4 way', '4-way', '4way'],
            nonNestingLidBoxKey: '25x25x15',
            nonNestingLidCapacity: 1,
        },
        {
            id: '55-round',
            family: '55-round',
            label: '55 gal round assembly',
            priority: 100,
            source: 'txt',
            confidence: 'high',
            finishedPartPrefixes: ['S8331A', 'S8332A', 'S8333A', 'S8334A', 'S8335A', 'S8336A'],
            descriptionIncludes: ['55', 'round'],
            bodyBoxKey: '25x25x38',
            lidBoxKey: '25x25x19',
            lidCapacity: 1,
            nestLidsWithBody: false,
        },
        {
            id: '22-square',
            family: '22-square',
            label: '22 gal square assembly',
            priority: 100,
            source: 'txt',
            confidence: 'high',
            finishedPartPrefixes: ['S7901A'],
            descriptionIncludes: ['22', '20 gallon', 'square'],
            bodyBoxKey: '15x15x22',
            lidBoxKey: '15x15x22',
            lidCapacity: 1,
            nestLidsWithBody: true,
        },
    ],
    palletPackingRules: DEFAULT_KOLORCANS_PALLET_PACKING_RULES,
};
